// @ts-strict-ignore
import {
  Button,
  ButtonVariant,
  IconName,
  Tooltip,
} from '@usealloy/component-library';
import React, {
  useCallback, useRef, useEffect, useMemo,
  useContext,
} from 'react';

import type { Action } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import {
  actionHasRules,
  findActionFromHighestPrecedence,
  getActionName,
  isDefaultPrecedenceIndexedAction,
  isPreviewOutcomeActionComplete,
  isPreviewWorkflowRoutingActionComplete,
} from 'flow/service/actions';
import PreviewActionTextDropdown from 'flow/editor/components/thresholds/FocusedActionText/PreviewActionTextDropdown';
import { selectFocusedAction } from 'flow/store/redux/selectors';
import { removeAction, setFocusedNodeId, setHoveredNodeId } from 'flow/store/redux/slice';
import OnIslandOutcomeNode from '~/dashboard/views/workflows/editor/components/Island/OutcomeIsland/OnIslandOutcomeNode';
import NodeTooltip from '../../Graph/nodes/BaseNode/NodeTooltip';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  StyledDefaultEditorText,
  StyledEditorContainer,
  StyledActionControlsContainer,
  StyledDecisioningCopyWrapper,
  StyledDecisioningWrapper,
  StyledRuleEditorContainer,
  StyledRuleHeaderContainer,
  StyledBadge,
  StyledSetOutcomeBadge,
} from './styles';
import OutcomeDropdown from './OutcomeDropdown';
import type { ActionTypeOption } from '../../thresholds/FocusedActionText/PreviewActionTextDropdown/types';
import WorkflowRoutingDropdown from './WorkflowRoutingDropdown';
import OnIslandWorkflowRoutingNode from '../WorkflowRoutingIsland/OnIslandWorkflowRoutingNode';
import { UserContext } from '~/dashboard/context';
import { selectEvaluationOutcome, selectThresholdOutputResults } from 'flow/store/redux/selectors/workflowFeatures';
import { StyledEvaluationSetTagNodeIndicator } from '../../Graph/nodes/BaseNode/BaseNode.styles';
import { ContainerType } from '../../NodeEditorDrawer/ThresholdsTab/types';
import RuleBuilderContainer from '../../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer';


interface IslandActionRuleEditorProps {
  action: Action;
  /**
   * This could be inferred from the actionType prop, but adding this manually to force
   * the developer to only pass actions of the support typed and have to be explicit about it
   */
  actionType: ActionType.OUTCOME | ActionType.WORKFLOW;
  allActionsOfActionType: Action[];
  isPreviewAction: boolean;
  onCancelAction: () => void;
  onSubmitAction: () => void;
  isInDiffMode?: boolean;
}

export default function IslandActionRuleEditor({
  action,
  actionType,
  allActionsOfActionType,
  isPreviewAction,
  onCancelAction,
  onSubmitAction,
  isInDiffMode,
}: IslandActionRuleEditorProps) {
  const { featureFlags } = useContext(UserContext);
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);
  const evaluationOutcome = useAppSelector(selectEvaluationOutcome);
  const ruleExplainabilityData = useAppSelector(selectThresholdOutputResults);

  const ref = useRef<HTMLDivElement>();
  const decisioningActionDropdownRef = useRef<any>();
  const lastFocusedActionPublicIdRef = useRef<typeof focusedAction['type']>();

  const { publicId } = action;
  const actionName = getActionName(action);
  const isFocused = focusedAction?.publicId === publicId && !isInDiffMode;
  const hasActionRules = actionHasRules(action);

  /**
   * While it's tempting to abstract all of the following pieces of derived state as props, this component
   * is only meant to work with either Outcome of Workflow Routing actions, and that is not going to change
   * for a long time, so it's not worth it. If we ever need to support a third action type, expose a
   * single object prop that has functions to compute of all this
   */
  const isActionTypeOutcome = actionType === ActionType.OUTCOME;
  const isOutcomeSet = isActionTypeOutcome && evaluationOutcome === action.outcome?.name;
  const isDefaultAction = isDefaultPrecedenceIndexedAction(action);


  let isActionBeingBuiltComplete = false;
  if (isPreviewAction) {
    isActionBeingBuiltComplete = isActionTypeOutcome
      ? isPreviewOutcomeActionComplete(action)
      : isPreviewWorkflowRoutingActionComplete(action);
  }

  const presetActionTypeOption: ActionTypeOption = isActionTypeOutcome
    ? ({
      label: 'decision to an outcome',
      value: 'decision to an outcome',
      meta: {
        singleValueLabel: 'Then Evaluations will be decisioned to ',
        type: ActionType.OUTCOME,
      },
    })
    : ({
      label: 'decision to a workflow',
      value: 'decision to a workflow',
      meta: {
        singleValueLabel: 'Then Evaluations will run through ',
        type: ActionType.WORKFLOW,
      },
    });


  const defaultActionCopy = useMemo(() => {
    const actionCopyMap = {
      [ActionType.OUTCOME]: {
        single: 'All evaluations will be decisioned to ',
        multiple: 'Else Evaluations will be decisioned to ',
      },
      [ActionType.WORKFLOW]: {
        single: 'All evaluations will run through ',
        multiple: 'Else Evaluations will run through ',
      },
    };

    const isSingle = allActionsOfActionType.length === 1;
    return actionCopyMap[actionType][isSingle ? 'single' : 'multiple'];
  }, [actionType, allActionsOfActionType]);

  const Node = isActionTypeOutcome
    ? OnIslandOutcomeNode
    : OnIslandWorkflowRoutingNode;

  const ActionDropdown = isActionTypeOutcome
    ? OutcomeDropdown
    : WorkflowRoutingDropdown;


  const handleSubmitAction = useCallback(() => {
    onSubmitAction();
  }, [onSubmitAction]);

  const handleCancelAction = useCallback(() => {
    dispatch(removeAction({ id: focusedAction.publicId }));

    const secondToLastPrecedenceIndexedAction = findActionFromHighestPrecedence(
      allActionsOfActionType, 1,
    );
    if (secondToLastPrecedenceIndexedAction) {
      dispatch(setFocusedNodeId(secondToLastPrecedenceIndexedAction.publicId));
    }
    onCancelAction();
  }, [
    allActionsOfActionType, dispatch, focusedAction, onCancelAction,
  ]);

  const handleNodeClick = useCallback(() => {
    if (!isOutcomeSet && featureFlags?.ruleExplainabilityPhase >= 4 && ruleExplainabilityData) {
      return;
    }
    if (focusedAction?.publicId !== publicId) {
      dispatch(setFocusedNodeId(publicId));
    }
  }, [dispatch, featureFlags, focusedAction?.publicId,
    isOutcomeSet, publicId, ruleExplainabilityData]);

  const handleNodeMouseEnter = useCallback(() => {
    dispatch(setHoveredNodeId(publicId));
  }, [dispatch, publicId]);

  const handleNodeMouseLeave = useCallback(() => {
    dispatch(setHoveredNodeId(null));
  }, [dispatch]);

  const onEscapeKeyDown = useCallback((_value, event) => {
    event.stopPropagation();
    event.preventDefault();
    event.target.blur();
  }, []);

  const onSwapOutcome = () => {
    setTimeout(() => decisioningActionDropdownRef?.current?.focus(), 100);
  };

  /**
   * Scrolls this component into the viewport when selected, but only if the
   * previous focused action was not of the same type. This ensures the scroll only
   * runs when the island opens and not while clicking through multiple
   * actions of the same type as this adds some jitter.
   */
  useEffect(() => {
    if (isFocused && lastFocusedActionPublicIdRef.current !== actionType) {
      ref.current.scrollIntoView({ block: 'end' });
    }

    lastFocusedActionPublicIdRef.current = focusedAction?.type;
  }, [actionType, isFocused, focusedAction?.type]);

  return (
    <StyledRuleEditorContainer
      ref={ref}
      isFocused={isInDiffMode ? undefined : isFocused}
      // Fading marks the un-focused cards while one is selected. Diff mode has no
      // selection, so every card would fade at once — keep them all at full opacity.
      isFaded={Boolean(focusedAction) && !isFocused && !isInDiffMode}
      onClick={isInDiffMode ? undefined : handleNodeClick}
    >
      <StyledRuleHeaderContainer>
        <Node
          action={action}
          allActionsOfActionType={allActionsOfActionType}
          isAddingNewAction={isPreviewAction}
          onSwapOutcome={onSwapOutcome}
          onClick={isInDiffMode ? undefined : handleNodeClick}
          onMouseEnter={isInDiffMode ? undefined : handleNodeMouseEnter}
          onMouseLeave={isInDiffMode ? undefined : handleNodeMouseLeave}
        />

        {isOutcomeSet && featureFlags?.ruleExplainabilityPhase >= 4 && (
          <StyledSetOutcomeBadge outline={false}>
            <StyledEvaluationSetTagNodeIndicator />
            Set
          </StyledSetOutcomeBadge>
        )}

        {isDefaultAction && (
          <Tooltip
            content={isActionTypeOutcome
              ? 'Default outcomes will be reached if no other outcome is reached.'
              : 'Default workflows will be run if no other workflow is reached'}
          >
            <StyledBadge icon={IconName.Info} data-testid="default-outcome-badge">
              Default
            </StyledBadge>
          </Tooltip>
        )}

      </StyledRuleHeaderContainer>

      <StyledEditorContainer
        rulesExist={(hasActionRules || isPreviewAction)}
        isPreviewAction={isPreviewAction}
      >
        {(hasActionRules || isPreviewAction) && (
          <RuleBuilderContainer
            sourceAction={action}
            containerType={ContainerType.ISLAND}
            isPreviewOutcome={isPreviewAction}
            // To avoid circular import, passing NodeTooltip into ThresholdsTab
            ActionHoverTooltip={props => (
              <NodeTooltip canClickToAction {...props} />
            )}
          />
        )}
        {isPreviewAction && (
          <>
            <div style={{ marginLeft: '2rem' }}>
              <PreviewActionTextDropdown
                isPreviewAction={isPreviewAction}
                isActionTypeOptionSelectDisabled
                action={action}
                presetActionTypeOptions={[presetActionTypeOption]}
                presetActionValueOptions={actionName ? [{
                  label: actionName,
                  value: actionName,
                }] : []}
                onKeyDownHandlers={{
                  Escape: onEscapeKeyDown,
                }}
              />
            </div>
            <StyledActionControlsContainer>
              <Button
                onClick={handleCancelAction}
                data-testid="cancelActionButton"
              >
                Cancel
              </Button>
              <Button
                disabled={!isActionBeingBuiltComplete}
                onClick={handleSubmitAction}
                variant={ButtonVariant.primary}
                data-testid="addActionButton"
              >
                Add
              </Button>
            </StyledActionControlsContainer>
          </>
        )}

        {isDefaultAction && (
          <StyledDefaultEditorText>
            {defaultActionCopy}
            <ActionDropdown
              isDefaultAction
              action={action}
              dataTestId="defaultAction"
            />
          </StyledDefaultEditorText>
        )}

        {!isDefaultAction && !isPreviewAction && (
          <StyledDecisioningWrapper>
            <StyledDecisioningCopyWrapper>
              Then Evaluations will be decisioned to
            </StyledDecisioningCopyWrapper>
            <ActionDropdown
              action={action}
              ref={decisioningActionDropdownRef}
              dataTestId="nonDefaultAction"
            />
          </StyledDecisioningWrapper>
        )}
      </StyledEditorContainer>
    </StyledRuleEditorContainer>
  );
}
